export const RESULT_STATUS_NORMALIZED = {
  DELAYED: "delayed",
  PENDING: "pending",
  ON_TIME: "on_time",
};

/**
 * @param {string} value
 * @returns {string} One of RESULT_STATUS_NORMALIZED
 */
export function normalizeResultStatus(value) {
  const normalized = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
  if (normalized === "delayed") return RESULT_STATUS_NORMALIZED.DELAYED;
  if (normalized === "pending") return RESULT_STATUS_NORMALIZED.PENDING;
  return RESULT_STATUS_NORMALIZED.ON_TIME;
}

/**
 * @param {string} sampleType
 * @returns {boolean}
 */
export function isProductSampleType(sampleType) {
  const normalized = String(sampleType || "").toLowerCase();
  return normalized === "product" || normalized.includes("product");
}

/**
 * @param {string} sampleType
 * @returns {boolean}
 */
export function isEnvironmentSampleType(sampleType) {
  const normalized = String(sampleType || "").toLowerCase();
  return normalized === "environment" || normalized.includes("environment");
}

/**
 * Parse API row dates as plain calendar days (PST-backed strings). No timezone math —
 * only the date token (YYYY-MM-DD, MM/DD/YYYY, or the date portion before "T").
 *
 * @param {string} dateValue
 * @returns {{ y: number, m: number, d: number } | null}
 */
export function parseSampleRowCalendarParts(dateValue) {
  const trimmed = String(dateValue || "").trim();
  if (!trimmed) return null;
  const dateOnly = trimmed.includes("T") ? trimmed.split("T")[0] : trimmed;

  const isoMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return {
      y: Number(isoMatch[1]),
      m: Number(isoMatch[2]),
      d: Number(isoMatch[3]),
    };
  }

  const usMatch = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return {
      y: Number(usMatch[3]),
      m: Number(usMatch[1]),
      d: Number(usMatch[2]),
    };
  }

  return null;
}

function padTwoDigits(value) {
  return String(value).padStart(2, "0");
}

function calendarPartsToSortKey(parts) {
  if (!parts) return 0;
  return parts.y * 10000 + parts.m * 100 + parts.d;
}

/**
 * Display only: MM/DD/YYYY from the same calendar fields the API sent (no conversions).
 *
 * @param {string} dateValue
 * @returns {string}
 */
export function formatSampleRowDateForDisplay(dateValue) {
  const parts = parseSampleRowCalendarParts(dateValue);
  if (!parts) {
    return String(dateValue || "").trim();
  }
  return `${padTwoDigits(parts.m)}/${padTwoDigits(parts.d)}/${parts.y}`;
}

/**
 * Stable sample identity for dedupe when genista_id is missing.
 *
 * @param {object} row
 * @returns {string}
 */
export function buildSampleInstanceKey(row) {
  const genista = row.genista_id != null && String(row.genista_id).trim() !== ""
    ? String(row.genista_id).trim()
    : "";
  if (genista) return genista;
  const sid = row.sample_id != null && String(row.sample_id).trim() !== ""
    ? String(row.sample_id).trim()
    : "";
  if (sid) return `sample:${sid}`;
  const wo = row.workorder_id != null && String(row.workorder_id).trim() !== ""
    ? String(row.workorder_id).trim()
    : "";
  return wo ? `wo:${wo}` : `anon:${JSON.stringify(row.test_pk)}:${JSON.stringify(row.date)}`;
}

/**
 * One sample-day → one count for Sample **line** (genista_id + date per FE spec).
 *
 * @param {object} row
 * @returns {string}
 */
export function buildSampleDayDedupeKey(row) {
  const displayDate = formatSampleRowDateForDisplay(row.date);
  return `${buildSampleInstanceKey(row)}|${displayDate}`;
}

/**
 * One sample per test category → one count for Sample **bar** (genista_id + test category; date ignored).
 *
 * @param {object} row
 * @returns {string}
 */
export function buildSampleCategoryBarDedupeKey(row) {
  const categoryKey = row.test_category || "Unknown";
  return `${buildSampleInstanceKey(row)}|${categoryKey}`;
}

/**
 * One test identity per row for test-level dedupe.
 *
 * @param {object} row
 * @returns {string}
 */
export function buildTestIdentityKey(row) {
  if (row.test_pk != null && row.test_pk !== "") {
    return `pk:${row.test_pk}`;
  }
  const name = String(row.test || "").trim();
  return `name:${name}`;
}

/**
 * One test calendar-day → one count for test trend line.
 *
 * @param {object} row
 * @returns {string}
 */
export function buildTestDayDedupeKey(row) {
  const displayDate = formatSampleRowDateForDisplay(row.date);
  return `${buildTestIdentityKey(row)}|${displayDate}`;
}

/**
 * When multiple API rows share the same test+day, prefer delayed, then pending, else on_time.
 *
 * @param {string} a
 * @param {string} b
 * @returns {string}
 */
function mergeResultStatusForSameTestDay(a, b) {
  const rank = (status) => {
    if (status === RESULT_STATUS_NORMALIZED.DELAYED) return 2;
    if (status === RESULT_STATUS_NORMALIZED.PENDING) return 1;
    return 0;
  };
  return rank(a) >= rank(b) ? a : b;
}

/**
 * Bar + table: test category on x-axis / first column — total, product, environment counts.
 * FE spec: Sample bar = **genista_id + test category** (each sample counts at most once per category
 * across the whole range, regardless of day or number of tests).
 *
 * @param {object[]} rows
 * @returns {Array<{ category: string, total: number, product: number, environment: number, test_category_pk?: number }>}
 */
export function buildSampleCategoryAggregates(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const bucketByCategory = safeRows.reduce((mapAccumulator, row) => {
    const categoryKey = row.test_category || "Unknown";
    if (!mapAccumulator.has(categoryKey)) {
      mapAccumulator.set(categoryKey, {
        category: categoryKey,
        total: 0,
        product: 0,
        environment: 0,
        test_category_pk: row.test_category_pk,
        barDedupeSeen: new Set(),
      });
    }
    const bucket = mapAccumulator.get(categoryKey);
    const dedupeKey = buildSampleCategoryBarDedupeKey(row);
    if (bucket.barDedupeSeen.has(dedupeKey)) {
      return mapAccumulator;
    }
    bucket.barDedupeSeen.add(dedupeKey);
    bucket.total += 1;
    if (isProductSampleType(row.sample_type)) {
      bucket.product += 1;
    } else if (isEnvironmentSampleType(row.sample_type)) {
      bucket.environment += 1;
    } else {
      bucket.product += 1;
    }
    return mapAccumulator;
  }, new Map());

  return Array.from(bucketByCategory.values())
    .map(({ barDedupeSeen, ...rest }) => rest)
    .sort((left, right) => left.category.localeCompare(right.category));
}

/**
 * Line chart: date on x-axis — product, environment, total sample counts per day (MM/DD/YYYY).
 * FE spec: Sample line = **genista_id + date** (one count per sample per calendar day, not per test row).
 *
 * @param {object[]} rows
 * @returns {Array<{ date: string, total: number, product: number, environment: number }>}
 */
export function buildSampleDateLineSeries(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const byDisplayDate = safeRows.reduce((mapAccumulator, row) => {
    const displayDate = formatSampleRowDateForDisplay(row.date);
    if (!mapAccumulator.has(displayDate)) {
      mapAccumulator.set(displayDate, {
        date: displayDate,
        total: 0,
        product: 0,
        environment: 0,
        sampleDaySeen: new Set(),
      });
    }
    const bucket = mapAccumulator.get(displayDate);
    const dedupeKey = buildSampleInstanceKey(row);
    if (bucket.sampleDaySeen.has(dedupeKey)) {
      return mapAccumulator;
    }
    bucket.sampleDaySeen.add(dedupeKey);
    bucket.total += 1;
    if (isProductSampleType(row.sample_type)) {
      bucket.product += 1;
    } else if (isEnvironmentSampleType(row.sample_type)) {
      bucket.environment += 1;
    } else {
      bucket.product += 1;
    }
    return mapAccumulator;
  }, new Map());

  return Array.from(byDisplayDate.values())
    .map(({ sampleDaySeen, ...rest }) => rest)
    .sort((left, right) => {
      const leftKey = calendarPartsToSortKey(parseSampleRowCalendarParts(left.date));
      const rightKey = calendarPartsToSortKey(parseSampleRowCalendarParts(right.date));
      return leftKey - rightKey;
    });
}

/**
 * Unique key for test + category (avoids collapsing distinct tests with the same display name).
 *
 * @param {object} row
 * @returns {string}
 */
export function buildTestAggregateRowKey(row) {
  const categoryPart = row.test_category_pk;
  const testPart = row.test_pk;
  return `${categoryPart}::${testPart}`;
}

/**
 * Table (+ source rows for category bar rollup): one row per test (+ category); **activity-based** —
 * each API row counts once toward totals and status buckets (original + retest rows both count).
 * Aligns with {@link buildTestCategoryDateFlatSeries}. Bar chart uses {@link buildTestCategoryRollupForBarChart}.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function buildTestAggregatesForTableAndBarChart(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const bucketByKey = safeRows.reduce((mapAccumulator, row) => {
    const mapKey = buildTestAggregateRowKey(row);
    if (!mapAccumulator.has(mapKey)) {
      mapAccumulator.set(mapKey, {
        key: mapKey,
        testCategory: row.test_category || "",
        testName: row.test || "",
        test_pk: row.test_pk,
        test_category_pk: row.test_category_pk,
        totalCount: 0,
        onTime: 0,
        delayed: 0,
        pending: 0,
        productCount: {
          total: 0,
          pending: 0,
          delayed: 0,
          onTime: 0,
        },
        envCount: {
          total: 0,
          pending: 0,
          delayed: 0,
          onTime: 0,
        },
        onTimeRows: [],
        delayedRows: [],
        pendingRows: [],
      });
    }
    const bucket = mapAccumulator.get(mapKey);
    const status = normalizeResultStatus(row.result_status);
    bucket.totalCount += 1;
    if (status === RESULT_STATUS_NORMALIZED.DELAYED) {
      bucket.delayed += 1;
      bucket.delayedRows.push(row);
    } else if (status === RESULT_STATUS_NORMALIZED.PENDING) {
      bucket.pending += 1;
      bucket.pendingRows.push(row);
    } else {
      bucket.onTime += 1;
      bucket.onTimeRows.push(row);
    }

    let typeKey;
    if (row.sample_belongs_to_type === "product") {
      typeKey = "productCount";
    } else if (row.sample_belongs_to_type === "environmental") {
      typeKey = "envCount";
    } else {
      typeKey = null;
    }
    if (typeKey) {
      bucket[typeKey].total += 1;
      if (status === RESULT_STATUS_NORMALIZED.DELAYED) {
        bucket[typeKey].delayed += 1;
      } else if (status === RESULT_STATUS_NORMALIZED.PENDING) {
        bucket[typeKey].pending += 1;
      } else {
        bucket[typeKey].onTime += 1;
      }
    }

    return mapAccumulator;
  }, new Map());

  return Array.from(bucketByKey.values())
    .sort((left, right) => {
      const byCategory = left.testCategory.localeCompare(right.testCategory);
      if (byCategory !== 0) return byCategory;
      return left.testName.localeCompare(right.testName);
    });
}

/**
 * Stable key for grouping per-test aggregate rows by test category (prefers primary key).
 *
 * @param {{ testCategory?: string, test_category_pk?: * }} perTestAggregateRow
 * @returns {string}
 */
export function buildTestCategoryRollupKey(perTestAggregateRow) {
  const row = perTestAggregateRow || {};
  if (row.test_category_pk != null && row.test_category_pk !== "") {
    return `pk:${String(row.test_category_pk)}`;
  }
  const label = (row.testCategory != null && String(row.testCategory).trim() !== "")
    ? String(row.testCategory).trim()
    : "Unknown";
  return `label:${label}`;
}

/**
 * Bar chart only: one row per test category — sums rows from
 * {@link buildTestAggregatesForTableAndBarChart} so category totals match the table.
 *
 * @param {Array<{ testCategory?: string, test_category_pk?: *, totalCount: number, onTime: number, delayed: number, pending: number }>} perTestAggregateRows
 * @returns {Array<{ key: string, testCategory: string, test_category_pk?: *, totalCount: number, onTime: number, delayed: number, pending: number }>}
 */
export function buildTestCategoryRollupForBarChart(perTestAggregateRows) {
  const safeRows = Array.isArray(perTestAggregateRows) ? perTestAggregateRows : [];

  const bucketByKey = safeRows.reduce((mapAccumulator, row) => {
    const mapKey = buildTestCategoryRollupKey(row);
    if (!mapAccumulator.has(mapKey)) {
      mapAccumulator.set(mapKey, {
        key: mapKey,
        testCategory: (row.testCategory != null && String(row.testCategory).trim() !== "")
          ? String(row.testCategory).trim()
          : "Unknown",
        test_category_pk: row.test_category_pk,
        totalCount: 0,
        onTime: 0,
        delayed: 0,
        pending: 0,
      });
    }
    const bucket = mapAccumulator.get(mapKey);
    bucket.totalCount += row.totalCount || 0;
    bucket.onTime += row.onTime || 0;
    bucket.delayed += row.delayed || 0;
    bucket.pending += row.pending || 0;
    return mapAccumulator;
  }, new Map());

  return Array.from(bucketByKey.values()).sort((left, right) => {
    if (left.test_category_pk != null && right.test_category_pk != null
        && Number(left.test_category_pk) !== Number(right.test_category_pk)) {
      return Number(left.test_category_pk) - Number(right.test_category_pk);
    }
    return left.testCategory.localeCompare(right.testCategory);
  });
}

/**
 * Line chart: date on x-axis — total, on_time, delayed, pending counts per day (MM/DD/YYYY).
 * Counts unique tests per calendar day (test_pk / test name + date); duplicate rows for the same
 * test on the same day merge to one; status uses worst of delayed > pending > on_time.
 *
 * @param {object[]} rows
 * @returns {Array<{ date: string, total: number, onTime: number, delayed: number, pending: number }>}
 */
export function buildTestDateLineSeries(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const statusByDateThenTest = safeRows.reduce((mapAccumulator, row) => {
    const displayDate = formatSampleRowDateForDisplay(row.date);
    const testDayKey = buildTestDayDedupeKey(row);
    const status = normalizeResultStatus(row.result_status);
    if (!mapAccumulator.has(displayDate)) {
      mapAccumulator.set(displayDate, new Map());
    }
    const testMap = mapAccumulator.get(displayDate);
    if (!testMap.has(testDayKey)) {
      testMap.set(testDayKey, status);
    } else {
      testMap.set(testDayKey, mergeResultStatusForSameTestDay(testMap.get(testDayKey), status));
    }
    return mapAccumulator;
  }, new Map());

  const byDisplayDate = new Map();
  statusByDateThenTest.forEach((testMap, displayDate) => {
    const bucket = {
      date: displayDate,
      total: 0,
      onTime: 0,
      delayed: 0,
      pending: 0,
    };
    testMap.forEach((status) => {
      bucket.total += 1;
      if (status === RESULT_STATUS_NORMALIZED.DELAYED) {
        bucket.delayed += 1;
      } else if (status === RESULT_STATUS_NORMALIZED.PENDING) {
        bucket.pending += 1;
      } else {
        bucket.onTime += 1;
      }
    });
    byDisplayDate.set(displayDate, bucket);
  });

  return Array.from(byDisplayDate.values()).sort((left, right) => {
    const leftKey = calendarPartsToSortKey(parseSampleRowCalendarParts(left.date));
    const rightKey = calendarPartsToSortKey(parseSampleRowCalendarParts(right.date));
    return leftKey - rightKey;
  });
}

/**
 * UI label for test result status (Results tab nested normalization).
 *
 * @param {string} normalizedStatus - RESULT_STATUS_NORMALIZED.*
 * @returns {"Pending" | "On Time" | "Delayed"}
 */
export function mapTestResultStatusToUiLabel(normalizedStatus) {
  if (normalizedStatus === RESULT_STATUS_NORMALIZED.DELAYED) return "Delayed";
  if (normalizedStatus === RESULT_STATUS_NORMALIZED.PENDING) return "Pending";
  return "On Time";
}

/**
 * Tests: group by calendar date → attach each activity row under its test_category (activity-based;
 * original + retest each appear; same test_name may repeat).
 * Shape:
 * `[{ date, test_category: { [categoryName]: [{ test_name, status: "Pending"|"On Time"|"Delayed" }] } }]`
 *
 * @param {object[]} rows
 * @returns {Array<{ date: string, test_category: Record<string, Array<{ test_name: string, status: string }>> }>}
 */
export function buildTestsNestedByDateTestCategory(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const byDate = new Map();

  safeRows.forEach((row) => {
    const displayDate = formatSampleRowDateForDisplay(row.date);
    const status = mapTestResultStatusToUiLabel(normalizeResultStatus(row.result_status));
    const testName = String(row.test || "").trim();
    const categoryName = row.test_category || "Unknown";

    if (!byDate.has(displayDate)) {
      byDate.set(displayDate, []);
    }
    byDate.get(displayDate).push({
      test_name: testName,
      test_category: categoryName,
      status,
    });
  });

  return Array.from(byDate.entries())
    .sort(([leftDate], [rightDate]) => {
      const leftKey = calendarPartsToSortKey(parseSampleRowCalendarParts(leftDate));
      const rightKey = calendarPartsToSortKey(parseSampleRowCalendarParts(rightDate));
      return leftKey - rightKey;
    })
    .map(([displayDate, entries]) => {
      /** @type {Record<string, Array<{ test_name: string, status: string }>>} */
      const testCategory = {};
      entries.forEach((item) => {
        const categoryName = item.test_category || "Unknown";
        if (!testCategory[categoryName]) {
          testCategory[categoryName] = [];
        }
        testCategory[categoryName].push({
          test_name: item.test_name,
          status: item.status,
        });
      });
      Object.keys(testCategory).forEach((categoryName) => {
        testCategory[categoryName].sort((left, right) => left.test_name.localeCompare(right.test_name));
      });
      return { date: displayDate, test_category: testCategory };
    });
}

/** Separates category name from metric in trend line {@link buildTestCategoryMetricLineKey}. */
const TEST_CATEGORY_METRIC_LINE_KEY_SEP = "\u001e";

/**
 * @param {string} categoryLabel
 * @param {'total_tests' | 'pending_count' | 'on_time_count' | 'delayed_count'} metricField
 * @returns {string}
 */
export function buildTestCategoryMetricLineKey(categoryLabel, metricField) {
  return `${categoryLabel}${TEST_CATEGORY_METRIC_LINE_KEY_SEP}${metricField}`;
}

/**
 * @param {string} lineDataKey
 * @returns {{ categoryLabel: string, metricField: string } | null}
 */
export function parseTestCategoryMetricLineKey(lineDataKey) {
  const stringKey = String(lineDataKey || "");
  const index = stringKey.indexOf(TEST_CATEGORY_METRIC_LINE_KEY_SEP);
  if (index === -1) {
    return null;
  }
  return {
    categoryLabel: stringKey.slice(0, index),
    metricField: stringKey.slice(index + TEST_CATEGORY_METRIC_LINE_KEY_SEP.length),
  };
}

export const TEST_LINE_METRIC_LABELS = {
  total_tests: "Total",
  pending_count: "Pending",
  on_time_count: "On Time",
  delayed_count: "Delayed",
};

/**
 * Per calendar date: activity-based roll-up for trend tooltip and sample-type lines (status + sample_type).
 *
 * @param {object[]} rows
 * @returns {Map<string, { pending_count: number, delayed_count: number, on_time_count: number, product_count: number, environment_count: number }>} displayDate → counts
 */
export function buildTestDateActivityRollup(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const byDisplayDate = new Map();

  safeRows.forEach((row) => {
    const displayDate = formatSampleRowDateForDisplay(row.date);
    if (!byDisplayDate.has(displayDate)) {
      byDisplayDate.set(displayDate, {
        pending_count: 0,
        delayed_count: 0,
        on_time_count: 0,
        product_count: 0,
        environment_count: 0,
      });
    }
    const bucket = byDisplayDate.get(displayDate);
    const status = normalizeResultStatus(row.result_status);
    if (status === RESULT_STATUS_NORMALIZED.DELAYED) {
      bucket.delayed_count += 1;
    } else if (status === RESULT_STATUS_NORMALIZED.PENDING) {
      bucket.pending_count += 1;
    } else {
      bucket.on_time_count += 1;
    }
    if (isProductSampleType(row.sample_type)) {
      bucket.product_count += 1;
    } else if (isEnvironmentSampleType(row.sample_type)) {
      bucket.environment_count += 1;
    } else {
      bucket.product_count += 1;
    }
  });

  return byDisplayDate;
}

/**
 * Per calendar date: test activity counts by product vs environmental sample_type (activity-based).
 *
 * @param {object[]} rows
 * @returns {Map<string, { product_count: number, environment_count: number }>} displayDate → counts
 */
export function buildTestDateSampleTypeActivityTotals(rows) {
  const rollup = buildTestDateActivityRollup(rows);
  const map = new Map();
  rollup.forEach((bucket, displayDate) => {
    map.set(displayDate, {
      product_count: bucket.product_count,
      environment_count: bucket.environment_count,
    });
  });
  return map;
}

/**
 * Line-chart aggregation: counts per (date, test category), **activity-based** — each API row adds one
 * toward totals and status buckets (original + retest both count).
 * Deduplicated-by-logical-test variant remains commented out below ({@link buildTestCategoryDateFlatSeriesDeduplicated}).
 *
 * @param {object[]} rows
 * @returns {Array<{ date: string, category: string, total_tests: number, pending_count: number, on_time_count: number, delayed_count: number, product_count: number, environment_count: number }>}
 */
export function buildTestCategoryDateFlatSeries(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const cellByKey = new Map();

  safeRows.forEach((row) => {
    const displayDate = formatSampleRowDateForDisplay(row.date);
    const categoryLabel = row.test_category || "Unknown";
    const cellKey = `${displayDate}||${categoryLabel}`;
    const status = normalizeResultStatus(row.result_status);

    if (!cellByKey.has(cellKey)) {
      cellByKey.set(cellKey, {
        date: displayDate,
        category: categoryLabel,
        total_tests: 0,
        pending_count: 0,
        on_time_count: 0,
        delayed_count: 0,
        product_count: 0,
        environment_count: 0,
      });
    }
    const cell = cellByKey.get(cellKey);
    cell.total_tests += 1;
    if (status === RESULT_STATUS_NORMALIZED.DELAYED) {
      cell.delayed_count += 1;
    } else if (status === RESULT_STATUS_NORMALIZED.PENDING) {
      cell.pending_count += 1;
    } else {
      cell.on_time_count += 1;
    }
    if (isProductSampleType(row.sample_type)) {
      cell.product_count += 1;
    } else if (isEnvironmentSampleType(row.sample_type)) {
      cell.environment_count += 1;
    } else {
      cell.product_count += 1;
    }
  });

  return Array.from(cellByKey.values()).sort((left, right) => {
    const dateCompare = calendarPartsToSortKey(parseSampleRowCalendarParts(left.date))
      - calendarPartsToSortKey(parseSampleRowCalendarParts(right.date));
    if (dateCompare !== 0) return dateCompare;
    return left.category.localeCompare(right.category);
  });
}

/** Pseudo-category for trend data summed across all test categories (one line per metric). */
export const TEST_TREND_AGGREGATE_CATEGORY_LABEL = "All tests";

/**
 * Sums {@link buildTestCategoryDateFlatSeries} rows by date so trend checkboxes map to metrics,
 * not one line per test category.
 *
 * @param {Array<{ date: string, total_tests?: number, pending_count?: number, on_time_count?: number, delayed_count?: number, product_count?: number, environment_count?: number }>} categoryFlatRows
 * @returns {Array<{ date: string, category: string, total_tests: number, pending_count: number, on_time_count: number, delayed_count: number, product_count: number, environment_count: number }>}
 */
export function buildTestDateAggregatedTrendFlatSeries(categoryFlatRows) {
  const safeRows = Array.isArray(categoryFlatRows) ? categoryFlatRows : [];
  const byDate = new Map();

  safeRows.forEach((row) => {
    const displayDate = row.date;
    if (!byDate.has(displayDate)) {
      byDate.set(displayDate, {
        date: displayDate,
        category: TEST_TREND_AGGREGATE_CATEGORY_LABEL,
        total_tests: 0,
        pending_count: 0,
        on_time_count: 0,
        delayed_count: 0,
        product_count: 0,
        environment_count: 0,
      });
    }
    const bucket = byDate.get(displayDate);
    bucket.total_tests += row.total_tests ?? 0;
    bucket.pending_count += row.pending_count ?? 0;
    bucket.on_time_count += row.on_time_count ?? 0;
    bucket.delayed_count += row.delayed_count ?? 0;
    bucket.product_count += row.product_count ?? 0;
    bucket.environment_count += row.environment_count ?? 0;
  });

  return Array.from(byDate.values()).sort((left, right) => {
    const leftKey = calendarPartsToSortKey(parseSampleRowCalendarParts(left.date));
    const rightKey = calendarPartsToSortKey(parseSampleRowCalendarParts(right.date));
    return leftKey - rightKey;
  });
}

/*
 * --- Deduplicated view (logical test per day + category; merged status). Not used; activity-based only. ---
 * To enable: rename to buildTestCategoryDateFlatSeries and remove the activity-based implementation above.
 *
 * export function buildTestCategoryDateFlatSeriesDeduplicated(rows) {
 *   const safeRows = Array.isArray(rows) ? rows : [];
 *   const cellByKey = new Map();
 *
 *   safeRows.forEach((row) => {
 *     const displayDate = formatSampleRowDateForDisplay(row.date);
 *     const categoryLabel = row.test_category || "Unknown";
 *     const testKey = buildTestIdentityKey(row);
 *     const cellKey = `${displayDate}||${categoryLabel}`;
 *     const status = normalizeResultStatus(row.result_status);
 *
 *     if (!cellByKey.has(cellKey)) {
 *       cellByKey.set(cellKey, {
 *         date: displayDate,
 *         category: categoryLabel,
 *         countsByTest: new Map(),
 *       });
 *     }
 *     const cell = cellByKey.get(cellKey);
 *     if (!cell.countsByTest.has(testKey)) {
 *       cell.countsByTest.set(testKey, status);
 *     } else {
 *       cell.countsByTest.set(
 *         testKey,
 *         mergeResultStatusForSameTestDay(cell.countsByTest.get(testKey), status),
 *       );
 *     }
 *   });
 *
 *   const flat = [];
 *   cellByKey.forEach((cell) => {
 *     let pendingCount = 0;
 *     let onTimeCount = 0;
 *     let delayedCount = 0;
 *     cell.countsByTest.forEach((mergedStatus) => {
 *       if (mergedStatus === RESULT_STATUS_NORMALIZED.DELAYED) delayedCount += 1;
 *       else if (mergedStatus === RESULT_STATUS_NORMALIZED.PENDING) pendingCount += 1;
 *       else onTimeCount += 1;
 *     });
 *     flat.push({
 *       date: cell.date,
 *       category: cell.category,
 *       total_tests: cell.countsByTest.size,
 *       pending_count: pendingCount,
 *       on_time_count: onTimeCount,
 *       delayed_count: delayedCount,
 *     });
 *   });
 *
 *   return flat.sort((left, right) => {
 *     const dateCompare = calendarPartsToSortKey(parseSampleRowCalendarParts(left.date))
 *       - calendarPartsToSortKey(parseSampleRowCalendarParts(right.date));
 *     if (dateCompare !== 0) return dateCompare;
 *     return left.category.localeCompare(right.category);
 *   });
 * }
 */

/**
 * Pivot flat category/date metrics into Recharts line data: one row per date with one key per (category × metric).
 *
 * @param {Array<{ date: string, category: string }>} flatRows
 * @param {Array<'total_tests' | 'pending_count' | 'on_time_count' | 'delayed_count'>} metricFields
 * @returns {{ chartRows: object[], lineDescriptors: Array<{ dataKey: string, categoryLabel: string, metricField: string, legendName: string, categoryIndex: number }> }}
 */
export function pivotTestCategoryFlatToLineChartByMetrics(flatRows, metricFields) {
  const safeRows = Array.isArray(flatRows) ? flatRows : [];
  const metrics = Array.isArray(metricFields) && metricFields.length > 0
    ? metricFields
    : ["total_tests"];

  const categoryNames = new Set();
  const dateSet = new Set();
  safeRows.forEach((row) => {
    categoryNames.add(row.category);
    dateSet.add(row.date);
  });

  const sortedCategories = Array.from(categoryNames).sort((left, right) => left.localeCompare(right));
  const sortedDates = Array.from(dateSet).sort((leftDate, rightDate) => {
    const leftKey = calendarPartsToSortKey(parseSampleRowCalendarParts(leftDate));
    const rightKey = calendarPartsToSortKey(parseSampleRowCalendarParts(rightDate));
    return leftKey - rightKey;
  });

  const lineDescriptors = [];
  sortedCategories.forEach((categoryLabel, categoryIndex) => {
    metrics.forEach((metricField) => {
      const label = TEST_LINE_METRIC_LABELS[metricField] ?? metricField;
      lineDescriptors.push({
        dataKey: buildTestCategoryMetricLineKey(categoryLabel, metricField),
        categoryLabel,
        metricField,
        legendName: label,
        categoryIndex,
      });
    });
  });

  const valueByDateCategoryMetric = new Map();
  safeRows.forEach((row) => {
    metrics.forEach((metricField) => {
      valueByDateCategoryMetric.set(
        `${row.date}||${row.category}||${metricField}`,
        row[metricField] ?? 0,
      );
    });
  });

  const chartRows = sortedDates.map((displayDate) => {
    const point = { date: displayDate };
    lineDescriptors.forEach(({ dataKey, categoryLabel, metricField }) => {
      const lookupKey = `${displayDate}||${categoryLabel}||${metricField}`;
      point[dataKey] = valueByDateCategoryMetric.has(lookupKey)
        ? valueByDateCategoryMetric.get(lookupKey)
        : 0;
    });
    return point;
  });

  return {
    chartRows,
    lineDescriptors,
  };
}

/**
 * @param {Array<{ date: string, category: string }>} flatRows
 * @param {'total_tests' | 'pending_count' | 'on_time_count' | 'delayed_count'} metricField
 * @returns {{ chartRows: object[], categoryNames: string[] }}
 */
export function pivotTestCategoryFlatToLineChartByMetric(flatRows, metricField) {
  const { chartRows, lineDescriptors } = pivotTestCategoryFlatToLineChartByMetrics(flatRows, [metricField]);
  const categoryNames = [...new Set(lineDescriptors.map((descriptor) => descriptor.categoryLabel))]
    .sort((left, right) => left.localeCompare(right));
  return {
    chartRows,
    categoryNames,
  };
}

/**
 * Tooltip lookup: (date × category) → full flat metric row.
 *
 * @param {Array<{ date: string, category: string, total_tests?: number, pending_count?: number, on_time_count?: number, delayed_count?: number, product_count?: number, environment_count?: number }>} flatRows
 * @returns {Map<string, { total_tests: number, pending_count: number, on_time_count: number, delayed_count: number, product_count: number, environment_count: number }>}
 */
export function buildTestCategoryFlatTooltipLookup(flatRows) {
  const lookup = new Map();
  const safeRows = Array.isArray(flatRows) ? flatRows : [];
  safeRows.forEach((row) => {
    const key = `${row.date}||${row.category}`;
    lookup.set(key, {
      total_tests: row.total_tests ?? 0,
      pending_count: row.pending_count ?? 0,
      on_time_count: row.on_time_count ?? 0,
      delayed_count: row.delayed_count ?? 0,
      product_count: row.product_count ?? 0,
      environment_count: row.environment_count ?? 0,
    });
  });
  return lookup;
}

/**
 * Rows for Sample detail modal: filter by test category.
 *
 * @param {object[]} allRows
 * @param {string | { category?: string, testCategoryPk?: number }} categoryOrOpts - label or { category, testCategoryPk }
 * @returns {object[]}
 */
export function filterSampleRowsByTestCategory(allRows, categoryOrOpts) {
  const category = typeof categoryOrOpts === "string"
    ? categoryOrOpts
    : (categoryOrOpts && categoryOrOpts.category);
  const testCategoryPk = typeof categoryOrOpts === "object" && categoryOrOpts != null
    ? categoryOrOpts.testCategoryPk
    : undefined;
  return allRows.filter((row) => {
    if (testCategoryPk != null && row.test_category_pk != null
      && Number(row.test_category_pk) === Number(testCategoryPk)) {
      return true;
    }
    if (testCategoryPk != null && row.test_category_pk != null) {
      return false;
    }
    return (row.test_category || "") === (category || "");
  });
}

/**
 * Rows for Test metrics modal: match category + test (prefers primary keys when provided).
 *
 * @param {object[]} allRows
 * @param {string | { testCategory?: string, testName?: string, testCategoryPk?: number, testPk?: number }} testCategoryOrOpts
 * @param {string} [testName] - when arg2 is string
 * @returns {object[]}
 */
export function filterSampleRowsByTestAndCategory(allRows, testCategoryOrOpts, testName) {
  let category;
  let name;
  let categoryPk;
  let testPk;

  if (typeof testCategoryOrOpts === "object" && testCategoryOrOpts != null) {
    category = testCategoryOrOpts.testCategory;
    name = testCategoryOrOpts.testName;
    categoryPk = testCategoryOrOpts.testCategoryPk;
    testPk = testCategoryOrOpts.testPk;
  } else {
    category = testCategoryOrOpts;
    name = testName;
  }

  return allRows.filter((row) => {
    const categoryMatch = categoryPk != null && row.test_category_pk != null
      ? Number(row.test_category_pk) === Number(categoryPk)
      : (row.test_category || "") === (category || "");
    if (!categoryMatch) return false;

    const testMatch = testPk != null && row.test_pk != null
      ? Number(row.test_pk) === Number(testPk)
      : (row.test || "") === (name || "");
    return testMatch;
  });
}

/**
 * Map API row to CustomerSubmissionDetailModal (byCategory variant) table row shape (QC fields defaulted; no QC in API yet).
 *
 * @param {object} row
 * @param {number} index
 * @returns {object}
 */
export function mapCustomerSampleRowToSampleModalRow(row, index) {
  const displayStatus = String(row.result_status || "-").replace(/_/g, " ");
  return {
    key: `${row.sample_id || index}-${row.genista_id || index}-${index}`,
    workorder_id: row.workorder_id,
    genista_id: row.genista_id,
    sample_id: row.sample_id,
    sample_type: row.sample_type,
    test: row.test,
    result: "-",
    test_status: displayStatus,
    qc_result: 1,
    manual_qc_result: 1,
    ml_qc_result: 1,
    ml_soft_qc_result: 1,
    ml_qc_correction: null,
    qc_status: "",
    ml_qc_status: "",
    manual_qc_status: "",
    spec: "-",
    due_by: formatSampleRowDateForDisplay(row.date),
    belongs_to: row.customer_name || "-",
    notes: row.is_retest ? "Retest" : "-",
  };
}

/**
 * Map API row to TestMetricsDetailModal table row shape.
 *
 * @param {object} row
 * @param {number} index
 * @returns {object}
 */
export function mapCustomerSampleRowToTestModalRow(row, index) {
  const status = normalizeResultStatus(row.result_status);
  const isDelayed = status === RESULT_STATUS_NORMALIZED.DELAYED;
  return {
    key: `${row.sample_id || index}-${row.genista_id || index}-${index}`,
    workorder_id: row.workorder_id,
    genista_id: row.genista_id,
    sample_id: row.sample_id,
    sample_type: row.sample_type,
    result: "-",
    test_status: String(row.result_status || "-").replace(/_/g, " "),
    isDelayed,
    delayed: isDelayed ? "Yes" : "No",
    qc_result: 1,
    manual_qc_result: 1,
    ml_qc_result: 1,
    ml_soft_qc_result: 1,
    ml_qc_correction: null,
    qc_status: "",
    ml_qc_status: "",
    manual_qc_status: "",
    spec: "-",
    due_by: formatSampleRowDateForDisplay(row.date),
    belongs_to: row.customer_name || "-",
    notes: row.is_retest ? "Retest" : "-",
  };
}

/**
 * Map GET /customer-submission-data row fields (handles Workorder_pk vs workorder_pk).
 *
 * @param {object} row
 * @returns {object}
 */
export function normalizeCustomerSubmissionApiRow(row) {
  if (!row || typeof row !== "object") {
    return {};
  }
  const copy = { ...row };
  if (copy.Workorder_pk != null && copy.workorder_pk == null) {
    copy.workorder_pk = copy.Workorder_pk;
  }
  return copy;
}

/**
 * Map GET /customer-sample-data rows for Sample / Test tabs.
 * Aggregations ({@link buildTestCategoryDateFlatSeries}, line charts, etc.) use `row.date`;
 * the API may only send `sample_received_date`.
 *
 * @param {object} row
 * @returns {object}
 */
export function normalizeCustomerSampleDataApiRow(row) {
  const base = normalizeCustomerSubmissionApiRow(row);
  const hasCanonicalDate = base.date != null && String(base.date).trim() !== "";
  if (hasCanonicalDate) {
    return base;
  }
  const received = base.sample_received_date;
  if (received != null && String(received).trim() !== "") {
    return { ...base, date: received };
  }
  return base;
}

/**
 * Map aggregate table test label (e.g. "Microbiology-Pathogen Screening") to submission API test_name_pk
 * (e.g. "Pathogen Screening"). If there is no "Category-" prefix, returns the trimmed string.
 *
 * @param {string} testNameFromAggregate
 * @returns {string}
 */
export function normalizeTestNameForSubmissionApi(testNameFromAggregate) {
  const raw = String(testNameFromAggregate || "").trim();
  if (!raw) return "";
  const prefixMatch = raw.match(/^[^-]+-(.+)$/);
  return prefixMatch ? prefixMatch[1].trim() : raw;
}

function deriveDelayedLabelsFromTestStatus(testStatus) {
  const statusText = String(testStatus || "").toLowerCase();
  if (statusText.includes("delay")) {
    return { isDelayed: true, delayed: "Yes" };
  }
  if (statusText.includes("pending")) {
    return { isDelayed: false, delayed: "No" };
  }
  if (statusText.includes("complete")) {
    return { isDelayed: false, delayed: "No" };
  }
  return { isDelayed: false, delayed: "-" };
}

/**
 * @param {object} row — raw GET /customer-submission-data row
 * @returns {Pick<object, 'received_date' | 'result_due_by' | 'result_sent_on' | 'days_taken_by_lab' | 'date_lab_uploaded' | 'days_taken_by_qc' | 'date_qc_taken' | 'days_overdue'>}
 */
function buildSubmissionDetailRowExtraFields(row) {
  const {
    result_due_by, result_sent_on, days_taken_by_lab, date_lab_uploaded, days_taken_by_qc, date_qc_taken, days_overdue,
  } = row;
  return {
    result_due_by, result_sent_on, days_taken_by_lab, date_lab_uploaded, days_taken_by_qc, date_qc_taken, days_overdue,
  };
}

/**
 * Map GET /customer-submission-data row → Sample detail modal table row.
 *
 * @param {object} row
 * @param {number} index
 * @returns {object}
 */
export function mapCustomerSubmissionRowToSampleDetailTableRow(row, index) {
  const normalized = normalizeCustomerSubmissionApiRow(row);
  const rawWorkOrderPk = normalized.workorder_pk ?? normalized.Workorder_pk;
  const workOrderPk = rawWorkOrderPk != null && rawWorkOrderPk !== "" ? rawWorkOrderPk : "";
  return {
    key: workOrderPk ? `submission-wo-pk-${workOrderPk}-${normalized.genista_id}-${normalized.test}` : `submission-${normalized.sample_id || index}-${normalized.genista_id}-${normalized.test}-${index}`,
    workorder_pk: workOrderPk !== "" ? rawWorkOrderPk : null,
    workorder_id: normalized.workorder_id,
    genista_id: normalized.genista_id,
    sample_id: normalized.sample_id,
    sample_type: normalized.sample_type,
    test: normalized.test,
    result: normalized.result != null && String(normalized.result).trim() !== ""
      ? String(normalized.result)
      : "-",
    test_status: normalized.test_status != null && String(normalized.test_status).trim() !== ""
      ? String(normalized.test_status)
      : "-",
    report_status: normalized.report_status,
    company: normalized.company != null && String(normalized.company).trim() !== ""
      ? String(normalized.company)
      : "",
    sample_belongs_to_type: normalized.sample_belongs_to_type != null && String(normalized.sample_belongs_to_type).trim() !== ""
      ? String(normalized.sample_belongs_to_type).trim()
      : "",
    workorder_created_by_name: normalized.workorder_created_by_name != null && String(normalized.workorder_created_by_name).trim() !== ""
      ? String(normalized.workorder_created_by_name).trim()
      : "",
    workorder_created_by_role_name: normalized.workorder_created_by_role_name != null && String(normalized.workorder_created_by_role_name).trim() !== ""
      ? String(normalized.workorder_created_by_role_name).trim()
      : "",
    qc_assigned_to: normalized.qc_assigned_to ?? null,
    manual_qc_action_taken_by: normalized.manual_qc_action_taken_by ?? null,
    is_retest: normalized.is_retest,
    belongs_to: normalized.belongs_to != null && String(normalized.belongs_to).trim() !== ""
      ? String(normalized.belongs_to).trim()
      : (normalized.customer_name || "-"),
    notes: normalized.is_retest != null && String(normalized.is_retest).trim() !== ""
      ? String(normalized.is_retest)
      : "-",
    received_date: normalized.received_date,
    ...buildSubmissionDetailRowExtraFields(row),
  };
}

/**
 * Map GET /customer-submission-data row → CustomerSubmissionDetailModal (byTest variant) table row.
 *
 * @param {object} row
 * @param {number} index
 * @returns {object}
 */
export function mapCustomerSubmissionRowToTestDetailTableRow(row, index) {
  const normalized = normalizeCustomerSubmissionApiRow(row);
  const { isDelayed, delayed } = deriveDelayedLabelsFromTestStatus(normalized.test_status);
  const rawWorkOrderPk = normalized.workorder_pk ?? normalized.Workorder_pk;
  const workOrderPk = rawWorkOrderPk != null && rawWorkOrderPk !== "" ? rawWorkOrderPk : "";
  return {
    key: workOrderPk ? `submission-wo-pk-${workOrderPk}-${normalized.genista_id}` : `submission-${normalized.sample_id || index}-${normalized.genista_id}-${index}`,
    workorder_pk: workOrderPk !== "" ? rawWorkOrderPk : null,
    workorder_id: normalized.workorder_id,
    genista_id: normalized.genista_id,
    sample_id: normalized.sample_id,
    sample_type: normalized.sample_type,
    test: normalized.test,
    result: normalized.result != null && String(normalized.result).trim() !== ""
      ? String(normalized.result)
      : "-",
    test_status: normalized.test_status != null && String(normalized.test_status).trim() !== ""
      ? String(normalized.test_status)
      : "-",
    report_status: normalized.report_status,
    company: normalized.company != null && String(normalized.company).trim() !== ""
      ? String(normalized.company)
      : "",
    sample_belongs_to_type: normalized.sample_belongs_to_type != null && String(normalized.sample_belongs_to_type).trim() !== ""
      ? String(normalized.sample_belongs_to_type).trim()
      : "",
    workorder_created_by_name: normalized.workorder_created_by_name != null && String(normalized.workorder_created_by_name).trim() !== ""
      ? String(normalized.workorder_created_by_name).trim()
      : "",
    workorder_created_by_role_name: normalized.workorder_created_by_role_name != null && String(normalized.workorder_created_by_role_name).trim() !== ""
      ? String(normalized.workorder_created_by_role_name).trim()
      : "",
    qc_assigned_to: normalized.qc_assigned_to ?? null,
    manual_qc_action_taken_by: normalized.manual_qc_action_taken_by ?? null,
    isDelayed,
    delayed,
    is_retest: normalized.is_retest,
    belongs_to: normalized.belongs_to != null && String(normalized.belongs_to).trim() !== ""
      ? String(normalized.belongs_to).trim()
      : (normalized.customer_name || "-"),
    notes: normalized.is_retest != null && String(normalized.is_retest).trim() !== ""
      ? String(normalized.is_retest)
      : "-",
    received_date: normalized.received_date,
    ...buildSubmissionDetailRowExtraFields(row),
  };
}
